/**
 * Shown in every data-deletion confirm step. Discloses that collaborative work
 * shared while on a team may persist in teammates' historical briefings even
 * after deletion: the backend removes the departing user's raw data, but the
 * de-identified summary context shared during membership can't be retracted.
 * See MVP bug-119 (audit #16) + the briefing-history anonymize-on-delete model.
 *
 * `mode` distinguishes the two delete paths, which differ in ONE material way:
 *  - 'account' (full account delete): the user leaves the team, so their name is
 *    anonymized to "a former member" in teammates' past briefings.
 *  - 'data' (data-only "Delete All", default): the user STAYS on the team, so
 *    their name is kept — only their raw data/conversations are purged. Saying
 *    "anonymized" here would be wrong, so the generic copy is used.
 */
export function DeletionResidueNotice({ mode = 'data' }: { mode?: 'account' | 'data' }) {
  return (
    <div
      style={{
        fontFamily: 'var(--sans)',
        fontSize: 12,
        lineHeight: 1.5,
        color: 'var(--text3)',
        background: 'var(--bg2)',
        border: '1px solid var(--border-b)',
        borderRadius: 6,
        padding: '10px 12px',
      }}
    >
      <strong style={{ color: 'var(--text2)' }}>A note on teams:</strong>{' '}
      {mode === 'account' ? (
        <>
          summaries of your work already shared in teammates&rsquo; historical briefings
          remain, anonymized &mdash; your name is replaced with &ldquo;a former member.&rdquo; We
          purge your raw data, but the de-identified collaborative context can&rsquo;t be retracted.
        </>
      ) : (
        <>
          work you shared with teammates while on a team may still appear in their historical
          briefings after deletion. We remove your individual entries, but collaborative context
          shared during your membership can&rsquo;t always be fully retracted.
        </>
      )}
    </div>
  );
}
